<!--
  Component: TabsContainer

  This component creates a container of tab buttons and tab content panels.
  Clicking on the tab buttons shows corresponding tab content panel.  The tab
  control is also intended to be keyboard accessible.

  Notes on Setup:
  - Hidden content will not calculate sizes correctly (e.g. for automatic calcs
      of size of DOM elements), so use isCalculatingLayout flag
  - If no inputNumTabs, it will use tabButtonIds then tabContentIds to
      determine number of tabs
  - If fewer tabButtonIds/tabContentIds than inputNumTabs, will create unique
      ids for any extra tabs

  Notes on Accessibility:
  - TAB-ing into tab control goes to currently selected tab
  - Up/down/left/right to move between tabs
  - TAB or SHIFT-TAB to exit the tab control.
  - TAB jumps to content first before links.
  - Focus border on tabs is only shown during keyboard navigation (not clicks)

  From mixin:
  - isCalculatingLayout - flag to toggle hidden method for CSS, to allow proper
      calculation of element sizes

  Props:
  - inputNumTabs - For creating tabs without tab button ids
  - inputTabIndex - For setting a selected tab from the outside
  - tabListClass - custom CSS class for list of tab buttons
  - tabListLabel - screenreader label for list of tab buttons
  - tabButtonIds - array of string ids for buttons, to access from outside
  - tabContentIds - array of string ids for content panels, to access outside
  - tabButtonClasses - array of string classes for buttons, to control styling
  - tabContentClasses - array of string classes for content, to control styling

  Events:
  - 'tab-selected' : i - passes index of selected tab via click/arrow key

  Slots:
  - tab-button-<i> - (i < numTabs) - content for i-th tab button
  - tab-content-<i> - (i < numTabs) - content for i-th tab panel
-->

<template>
  <div class="tabs-container">
    <div :class="['tab-button-bar',tabListClass,isKeyboardNav?'kb-nav':'']"
      role="tablist"
      :aria-label="tabListLabel"
      >
      <div v-for="(n,i) in numTabs"
        :key="i"
        :id="tabButtonIdForIndex(i)"
        :ref="tabButtonIdForIndex(i)"
        :class="['tab-button',tabButtonClassForIndex(i),tabIsSelected(i)?'selected':'']"
        role="tab"
        :aria-controls="tabContentIdForIndex(i)"
        :aria-selected="tabIsSelected(i)?'true':'false'"
        :tabindex="tabIsSelected(i)?0:-1"
        @click="onClickTab(i)"
        @focus="onFocusTab(i)"
        @blur="onBlurTab"
        @mousedown="onMouseDownTab"
        @mouseup="onMouseUpTab"
        @keyup.left="onArrowLeftPressed"
        @keyup.right="onArrowRightPressed"
        @keydown.up.prevent="onArrowUpPressed"
        @keydown.down.prevent="onArrowDownPressed"
        @keydown.tab.exact.prevent="onTabPressed"
        >
        <slot :name="`tab-button-${i}`"></slot>
      </div>
    </div>

    <template v-for="(n,i) in numTabs">
      <div :key="`tph-${i}`" :class="['tab-print-header print-only',isCalculatingLayout?'calc-hidden':'',tabIsSelected(i)?'selected':'']">
        <slot :name="`tab-print-header-${i}`"></slot>
      </div>
      <div
        :key="`tc-${i}`"
        :id="tabContentIdForIndex(i)"
        :ref="tabContentIdForIndex(i)"
        :class="['tab-content',tabContentClassesForIndex(i)]"
        role="tabpanel"
        :aria-labelledby="tabButtonIdForIndex(i)"
        tabindex="-1"
        >
        <slot :name="`tab-content-${i}`"></slot>
      </div>
    </template>
  </div>
</template>

<script>
import Component, { mixins } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

import { LayoutMixin } from '@/mixin'

@Component({
  props: {
    inputNumTabs: Number,
    inputTabIndex: Number,
    tabListClass: String,
    tabListLabel: String,
    tabButtonIds: Array,
    tabContentIds: Array,
    tabButtonClasses: Array,
    tabContentClasses: Array
  }
})
export default class TabsContainer extends mixins(LayoutMixin) {
  isClicking = false
  isKeyboardNav = false
  isLoadingInputTab = false
  selectedTabIndex = 0 // default to first tab

  /* Lifecycle */

  mounted () {
    // Sync to parent index on load, if it exists
    if (this.inputTabIndex !== undefined) {
      this.selectedTabIndex = this.inputTabIndex
    }
  }

  /* Watchers */

  @Watch('inputTabIndex')
  onInputTabIndexChange (newVal, oldVal) {
    if (this.selectedTabIndex === newVal) return
    this.isLoadingInputTab = true
    this.selectedTabIndex = newVal
    this.updateTabFocus()
    // Focus update is async, so clear isLoadingInputTab flag in onFocusTab
  }

  /* Getters */

  get numTabs () {
    let n = 0
    if (this.inputNumTabs !== undefined) {
      n = this.inputNumTabs
    } else if (this.tabButtonIds) {
      n = this.tabButtonIds.length
    } else if (this.tabContentIds) {
      n = this.tabContentIds.length
    }
    return n
  }
  get selectedTabContentId () {
    return this.tabContentIdForIndex(this.selectedTabIndex)
  }
  get selectedTabButtonId () {
    return this.tabButtonIdForIndex(this.selectedTabIndex)
  }

  /* Event Handlers */

  onClickTab (i) {
    this.handleTabSelected(i)
  }
  onFocusTab (i) {
    this.isKeyboardNav = !this.isClicking && !this.isLoadingInputTab
    this.isLoadingInputTab = false
  }
  onBlurTab () {
    this.isKeyboardNav = false
  }
  onMouseDownTab () {
    this.isKeyboardNav = false
    this.isClicking = true
  }
  onMouseUpTab () {
    this.isClicking = false
  }
  onArrowLeftPressed () {
    this.selectPreviousTab()
  }
  onArrowRightPressed () {
    this.selectNextTab()
  }
  // Handle on keydown on up/down arrows to prevent default behavior
  //  so browser doesn't scroll
  onArrowUpPressed () {
    this.selectPreviousTab()
  }
  onArrowDownPressed () {
    this.selectNextTab()
  }
  // Only handle exactly tab (not shift-tab), and prevent default behavior of
  //  focusing of links to jump to top of content first before any links
  onTabPressed () {
    this.focusElementId(this.selectedTabContentId)
  }

  /* Methods - utility */

  focusElementId (id) {
    this.$refs[id][0].focus()
  }
  updateTabFocus () {
    this.focusElementId(this.selectedTabButtonId)
  }
  handleTabSelected (i) {
    if (this.selectedTabIndex === i) return
    this.selectedTabIndex = i
    this.$emit('tab-selected', i)
  }
  selectPreviousTab () {
    this.isKeyboardNav = true
    if (this.selectedTabIndex > 0) {
      this.handleTabSelected(this.selectedTabIndex - 1)
      this.updateTabFocus()
    }
  }
  selectNextTab () {
    this.isKeyboardNav = true
    if (this.selectedTabIndex < this.numTabs - 1) {
      this.handleTabSelected(this.selectedTabIndex + 1)
      this.updateTabFocus()
    }
  }
  tabIsSelected (i) {
    return this.selectedTabIndex === i
  }
  itemForIndex (a, i) {
    return a ? a[i] : undefined
  }
  tabUniqueIdForType (type, i) {
    return `tab-${this._uid}-${type}-${i}`
  }
  tabButtonIdForIndex (i) {
    return this.itemForIndex(this.tabButtonIds, i) || this.tabUniqueIdForType('button', i)
  }
  tabContentIdForIndex (i) {
    return this.itemForIndex(this.tabContentIds, i) || this.tabUniqueIdForType('content', i)
  }
  tabButtonClassForIndex (i) {
    return this.itemForIndex(this.tabButtonClasses, i)
  }
  tabContentClassesForIndex (i) {
    const tcClasses = []
    const inputClasses = this.itemForIndex(this.tabContentClasses, i)
    if (inputClasses !== undefined) {
      tcClasses.push(inputClasses)
    }
    if (!this.tabIsSelected(i)) {
      tcClasses.push(this.hiddenClass)
    }
    return tcClasses.join(' ')
  }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
.tab-button-bar {
  display: flex;
}
.tab-button {
  color: #555555;
  flex: 1;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  padding-top: 12px;
  padding-bottom: 12px;
  height: 2em;
  &.selected {
    color: black;
  }
}
.tab-content {
  padding: 0px;
  min-height: 300px;
}
.tab-button-bar:not(.kb-nav) .tab-button:focus,
.tab-content:focus {
  outline: none;
}
</style>



// WEBPACK FOOTER //
// src/components/TabsContainer.vue